import { useMemo, useEffect, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import redux, { api } from '../'
import { busy, notBusy } from '../busy/reducer'
import { makeSelectMetadataByRequestId, selectStatusByRequestId } from '../busy/selectors'
import { handleGetErrors, handleAddErrors, handleDeleteErrors, handleUpdateErrors } from '../error/actions'
import { useRequestId } from '../../hooks/useRequestId'
import { makeSelectorById, makeSelectorTicketByProjectId } from './selectors'

const { actions } = redux

export function useGetTicketByIdQuery (id) {
  const reqId = useRequestId(id)
  const dispatch = useDispatch()
  const [refetching, setRefetching] = useState(false)
  const selectTicketById = useMemo(makeSelectorById, [])

  const status = useSelector((state) => selectStatusByRequestId(state, reqId))
  const data = useSelector((state) => selectTicketById(state, id))

  const effects = useCallback((requestId, params) => {
    dispatch(busy({ requestId }))
    api.getTickets(params).then(result => {
      dispatch(actions.ticket.receivedTickets(result.data.value))
      dispatch(notBusy({ requestId }))
    }).catch(handleGetErrors('ticket', dispatch, requestId))
  }, [dispatch])

  useEffect(() => {
    if (status === undefined || refetching) {
      effects(reqId, { query: [{ key: 'id', op: '=', value: id }] })
      setRefetching(false)
    }
  }, [effects, id, reqId, status, refetching, setRefetching])

  const refetch = useCallback(() => {
    setRefetching(true)
  }, [setRefetching])

  const isLoading = status === 'pending' || status === undefined
  const isError = status === 'rejected'
  const isSuccessful = status === 'fulfilled'
  const isUnitialized = status === undefined
  return { isUnitialized, isError, isSuccessful, data, refetch, isLoading }
}

export function useGetTicketByProjectIdQuery (id) {
  const reqId = useRequestId()
  const dispatch = useDispatch()
  const [refetching, setRefetching] = useState(false)
  const selectTickets = useMemo(makeSelectorTicketByProjectId, [])

  const status = useSelector((state) => selectStatusByRequestId(state, reqId))
  const data = useSelector((state) => selectTickets(state, id))

  const effects = useCallback((requestId, params) => {
    dispatch(busy({ requestId }))
    api.getTickets(params).then(result => {
      dispatch(actions.ticket.receivedTickets(result.data.value))
      dispatch(notBusy({ requestId }))
    }).catch(handleGetErrors('ticket', dispatch, requestId))
  }, [dispatch])

  useEffect(() => {
    if (!id) {
      return
    }
    if (status === undefined || refetching) {
      effects(reqId, { query: [{ key: 'project_id', op: '=', value: id }], sort: 'expires_at.asc' })
      setRefetching(false)
    }
  }, [effects, id, reqId, status, refetching, setRefetching])

  const refetch = useCallback(() => {
    setRefetching(true)
  }, [setRefetching])

  const isLoading = status === 'pending' || status === undefined
  const isError = status === 'rejected'
  const isSuccessful = status === 'fulfilled'
  const isUnitialized = status === undefined
  return { isUnitialized, isError, isSuccessful, data, refetch, isLoading }
}

export function useAddTicketMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()
  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])

  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((ticket) => {
    dispatch(busy({ requestId }))
    api.addTicket(ticket).then(result => {
      dispatch(notBusy({ requestId, id: result.data.value?.id }))
    }).catch(handleAddErrors('ticket', dispatch))
  }, [dispatch, requestId])

  const isUnitialized = status === 'pending' || status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'
  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}

export function useUpdateTicketMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()

  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])
  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((ticket) => {
    dispatch(busy({ requestId }))
    api.updateTicket(ticket).then(result => {
      dispatch(notBusy({ requestId, id: result.data.value?.id }))
    }).catch(handleUpdateErrors('ticket', dispatch, requestId))
  }, [dispatch, requestId])

  const isUnitialized = status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}

export function useDeleteTicketMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()

  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])
  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((ticket) => {
    dispatch(busy({ requestId }))
    api.deleteTicket(ticket).then(() => {
      dispatch(notBusy({ requestId }))
      dispatch(actions.ticket.getTickets())
    }).catch(handleDeleteErrors('ticket', dispatch, requestId))
  }, [dispatch, requestId])

  const isUnitialized = status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}

export function useFavoriteTicketMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()

  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])
  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((ticket) => {
    const favorite = !ticket.is_favorite
    dispatch(busy({ requestId }))
    api.setTicketFavorite(ticket.id, favorite).then(() => {
      dispatch(notBusy({ requestId }))
    }).catch(handleDeleteErrors('ticket', dispatch, requestId))
  }, [dispatch, requestId])

  const isUnitialized = status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}
